/**
 * Hook para buscar conversas de um contato específico
 * Usado na aba Chat do modal de negócios premium
 *
 * Versão: 2.0.0
 * - Refatoração 100% server-side: substitui o SELECT direto em
 *   `conversations` pela RPC `get_deal_chat_conversations`, que valida
 *   tenant ativo e ownership do contato (SECURITY DEFINER).
 * - Mantém o mesmo shape `ContactConversation` para evitar mudanças no
 *   componente `DealChatTab`.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('ContactConversations');

// Interface da conversa retornada (consumida pela UI)
export interface ContactConversation {
  id: string;
  status: string;
  last_message_preview: string | null;
  last_message_at: string | null;
  unread_count: number;
  inbox: {
    id: string;
    name: string;
    channel_type: string;
    identifier: string | null; // Número do WhatsApp/telefone da inbox
  } | null;
}

// Opções de configuração do hook
export interface ContactConversationsOptions {
  enabled?: boolean;
}

/**
 * Hook para buscar todas as conversas de um contato.
 * Suporta lazy loading via options.enabled.
 * Fonte de dados: RPC `get_deal_chat_conversations` (server-side).
 */
export function useContactConversations(contactId: string | null, options?: ContactConversationsOptions) {
  const { currentTenant } = useAuth();

  // Determina se query deve executar (lazy loading)
  const isEnabled = (options?.enabled ?? true) && !!contactId && !!currentTenant?.id;

  return useQuery({
    queryKey: ['contact-conversations', contactId],
    queryFn: async () => {
      if (!contactId || !currentTenant?.id) return [] as ContactConversation[];

      logger.debug('Buscando conversas do contato via RPC:', contactId);

      // Chamada server-side — sem SELECT direto em conversations
      const { data, error } = await (supabase.rpc as any)('get_deal_chat_conversations', {
        p_contact_id: contactId,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar conversas do contato:', error.message);
        throw error;
      }

      // Mapeia o retorno flat da RPC para o shape consumido pela UI
      const rows = (data || []) as Array<{
        id: string;
        status: string;
        last_message_preview: string | null;
        last_message_at: string | null;
        unread_count: number | null;
        inbox_id: string | null;
        inbox_name: string | null;
        channel_type: string | null;
        inbox_identifier: string | null;
      }>;

      const mapped: ContactConversation[] = rows.map((row) => ({
        id: row.id,
        status: row.status,
        last_message_preview: row.last_message_preview,
        last_message_at: row.last_message_at,
        unread_count: row.unread_count ?? 0,
        inbox: row.inbox_id
          ? {
              id: row.inbox_id,
              name: row.inbox_name ?? '',
              channel_type: row.channel_type ?? '',
              identifier: row.inbox_identifier,
            }
          : null,
      }));

      logger.debug(`Encontradas ${mapped.length} conversas para o contato`);
      return mapped;
    },
    enabled: isEnabled,
    staleTime: 30 * 1000, // Cache de 30 segundos
  });
}
